import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import {
  alongLineM,
  capturedRooms,
  dominantAxisDeg,
  nearestVertex,
  planDistanceM,
  planLineAngleDeg,
  planLineLengthM,
  resizedLine,
  snapPlanPoint,
  straightenedLine,
  surfacePlanLine,
  type OpeningModifyData,
  type PlanEdit,
  type PlanLine,
  type PlanOpeningKind,
  type PlanPoint,
  type PlanRoom,
  type PlanSegment,
  type PlanSnap,
} from "@sts/geometry";
import { wallsFacingRooms } from "@sts/calc";
import type { PlacedGeometry } from "@sts/rules-data";
import type { PlanEditBatch, PlanObject, PlanObjectPatch } from "../api/types.js";
import { newDimensionId } from "./plan-annotations.js";
import {
  OPENING_DEFAULTS,
  deleteBatch,
  followWalls,
  inverseBatch,
  isEmptyBatch,
  lineOf,
  movedWalls,
  openingEdit,
  openingOffsetM,
  recordFor,
  roomAt,
  roomEdit,
  wallPropsEdit,
  wallRunEdit,
  type ObjectFollow,
} from "./plan-editor.js";
import type { PlanEditLayer } from "./use-plan-edits.js";

/**
 * The floor-plan editor's state and gestures — split out of `FloorPlan.tsx`,
 * which draws the plan and owns the other tools.
 *
 * Asked for on 2026-09-11: Polycam-style correction of what the scan got
 * wrong. With "Edit plan" on:
 *
 *   - press a wall and drag: it moves square to itself (Shift: any
 *     direction), and the walls it shares corners with stretch to stay joined
 *     (Alt: move it alone). Its windows and doors, and the radiators and
 *     outdoor unit against it, go with it;
 *   - drag a selected wall's end handle (A or B): that corner moves, snapping
 *     to other corners and to square;
 *   - press a window, door or opening and drag: it slides along its wall;
 *   - the side panel types an exact length, thickness, height, width or sill,
 *     straightens a wall, deletes, restores and reverts to the scan, and
 *     renames the room whose floor is selected;
 *   - Add wall (two clicks) and Door / Window / Opening (one click on a wall)
 *     are one-shot tools, like every other tool on the plan.
 *
 * Every change is one record set (`PlanEditBatch`) sent with
 * `PlanEditLayer.state.commit`; undo and redo (Ctrl/Cmd+Z, Ctrl/Cmd+Shift+Z or
 * Ctrl+Y) replay the record set's inverse, within this session. Objects that
 * followed a wall are moved back with it.
 */

export type EditTool =
  | { readonly kind: "wall" }
  | { readonly kind: "opening"; readonly opening: PlanOpeningKind }
  /** "Split room": a line drawn across a room, wall to wall, the way a wall is added. */
  | { readonly kind: "split" };

export type LengthAnchor = "start" | "centre" | "end";

interface UndoEntry {
  readonly label: string;
  readonly forward: PlanEditBatch;
  readonly backward: PlanEditBatch;
  readonly objects: readonly ObjectFollow[];
}

type EditDrag =
  | {
      readonly kind: "wall";
      readonly wallId: string;
      readonly grab: PlanPoint;
      readonly clientX: number;
      readonly clientY: number;
      moved: boolean;
    }
  | {
      readonly kind: "end";
      readonly wallId: string;
      readonly which: "start" | "end";
      readonly grab: PlanPoint;
      readonly clientX: number;
      readonly clientY: number;
      moved: boolean;
    }
  | {
      readonly kind: "opening";
      readonly openingId: string;
      readonly hostId: string;
      /** Pointer's distance along the wall minus the opening's centre's, at the press. */
      readonly grabOffsetM: number;
      readonly clientX: number;
      readonly clientY: number;
      moved: boolean;
    };

export interface EditPreview {
  readonly batch: PlanEditBatch;
  readonly objects: ReadonlyMap<string, PlacedGeometry>;
}

export interface OpeningGhost {
  readonly kind: PlanOpeningKind;
  readonly wall: PlanSegment;
  readonly start: PlanPoint;
  readonly end: PlanPoint;
  readonly offsetM: number;
  readonly widthM: number;
}

export interface PlanEditorInput {
  readonly layer: PlanEditLayer | undefined;
  /** This storey's rooms as drawn now (the effective plan, without a drag's preview). */
  readonly rooms: readonly PlanRoom[];
  readonly objects: readonly PlanObject[];
  readonly updateObject: (id: string, patch: PlanObjectPatch) => void;
  readonly activeStory: number;
  readonly selectedId: string | undefined;
  readonly onSelect: (id: string | undefined) => void;
  readonly planAt: (clientX: number, clientY: number) => PlanPoint | null;
  /** The snapping radius, metres, at the current zoom. */
  readonly snapRadiusM: () => number;
  /** A drag is not a click: the plan must not also deselect where it ended. */
  readonly swallowNextClick: () => void;
  /** An editor tool was picked up: every other tool on the plan goes down. */
  readonly onToolArmed: () => void;
}

const DRAG_THRESHOLD_PX = 4;
/** Within this of square (or 45°) from the storey's main axis, a dragged corner snaps onto it. */
const AXIS_SNAP_DEG = 4;

/** An id for a wall or opening added on the plan: a UUID where the browser has one. */
function newElementId(): string {
  const id = newDimensionId();
  return id.startsWith("dim-") ? `el-${id.slice(4)}` : id;
}

export function usePlanEditor(input: PlanEditorInput) {
  const { layer, rooms, activeStory, selectedId } = input;
  const records = layer?.state.records ?? [];
  const available = !!layer && layer.canEdit && layer.state.status === "ready";

  const [editing, setEditingState] = useState(false);
  const [tool, setTool] = useState<EditTool | null>(null);
  const [pendingStart, setPendingStart] = useState<PlanPoint | null>(null);
  const [hover, setHover] = useState<PlanSnap | null>(null);
  const [preview, setPreview] = useState<EditPreview | null>(null);
  const [undoStack, setUndoStack] = useState<readonly UndoEntry[]>([]);
  const [redoStack, setRedoStack] = useState<readonly UndoEntry[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const dragRef = useRef<EditDrag | null>(null);
  const previewRef = useRef<EditPreview | null>(null);

  const walls = useMemo(
    () => rooms.flatMap((room) => room.segments.filter((s) => s.kind === "wall")),
    [rooms],
  );
  const openings = useMemo(
    () => rooms.flatMap((room) => room.segments.filter((s) => s.kind !== "wall")),
    [rooms],
  );
  const facing = useMemo(() => wallsFacingRooms(rooms), [rooms]);
  const storeyObjects = useMemo(
    () => input.objects.filter((o) => o.storey === activeStory),
    [input.objects, activeStory],
  );
  const axisDeg = useMemo(() => dominantAxisDeg(walls.map(lineOf)), [walls]);

  // The editor is only ever on where it can be used.
  useEffect(() => {
    if (!available && editing) setEditingState(false);
  }, [available, editing]);

  // A different storey is a different drawing: nothing half-drawn carries over.
  useEffect(() => {
    setPendingStart(null);
    setHover(null);
    setTool(null);
  }, [activeStory]);

  const setEditing = useCallback((on: boolean) => {
    setEditingState(on);
    setTool(null);
    setPendingStart(null);
    setHover(null);
    setNotice(null);
    dragRef.current = null;
    previewRef.current = null;
    setPreview(null);
  }, []);

  const armTool = useCallback(
    (next: EditTool | null) => {
      setPendingStart(null);
      setHover(null);
      setTool(next);
      if (next) input.onToolArmed();
    },
    [input],
  );

  // ---------------------------------------------------------- changes ----

  /** Sends one change, remembers how to undo it, and moves the objects that follow. */
  const change = useCallback(
    (label: string, forward: PlanEditBatch, objects: readonly ObjectFollow[] = []): void => {
      if (!layer || isEmptyBatch(forward)) return;
      const entry: UndoEntry = { label, forward, backward: inverseBatch(layer.state.records, forward), objects };
      setUndoStack((stack) => [...stack.slice(-99), entry]);
      setRedoStack([]);
      for (const follow of objects) input.updateObject(follow.id, { geometry: follow.after });
      void layer.state.commit(forward).then((ok) => {
        if (ok) return;
        // Refused: the plan went back, so the objects go back, and there is nothing to undo.
        for (const follow of objects) input.updateObject(follow.id, { geometry: follow.before });
        setUndoStack((stack) => stack.filter((e) => e !== entry));
      });
    },
    [layer, input],
  );

  const undo = useCallback(() => {
    if (!layer) return;
    const entry = undoStack[undoStack.length - 1];
    if (!entry) return;
    setUndoStack((stack) => stack.slice(0, -1));
    setRedoStack((stack) => [...stack, entry]);
    for (const follow of entry.objects) input.updateObject(follow.id, { geometry: follow.before });
    // As a replay: refused if a colleague has changed those walls since,
    // rather than undoing their work. Refused, the walls stayed, so the
    // objects that followed them stay too, and the step is gone from history.
    void layer.state.commit(entry.backward, { replay: true }).then((ok) => {
      if (ok) return;
      for (const follow of entry.objects) input.updateObject(follow.id, { geometry: follow.after });
      setRedoStack((stack) => stack.filter((e) => e !== entry));
    });
    setNotice(`Undone: ${entry.label}.`);
  }, [layer, undoStack, input]);

  const redo = useCallback(() => {
    if (!layer) return;
    const entry = redoStack[redoStack.length - 1];
    if (!entry) return;
    setRedoStack((stack) => stack.slice(0, -1));
    setUndoStack((stack) => [...stack, entry]);
    for (const follow of entry.objects) input.updateObject(follow.id, { geometry: follow.after });
    void layer.state.commit(entry.forward, { replay: true }).then((ok) => {
      if (ok) return;
      for (const follow of entry.objects) input.updateObject(follow.id, { geometry: follow.before });
      setUndoStack((stack) => stack.filter((e) => e !== entry));
    });
    setNotice(`Redone: ${entry.label}.`);
  }, [layer, redoStack, input]);

  const wallById = useCallback((id: string) => walls.find((w) => w.id === id), [walls]);
  const openingById = useCallback((id: string) => openings.find((o) => o.id === id), [openings]);

  /** Walls whose ends move to `moves` (their shared corners with them), as records and followers. */
  const wallMoveChange = useCallback(
    (moves: { wallId: string; which: "start" | "end"; to: PlanPoint }[], detached = false) => {
      const moved = movedWalls(walls, moves, detached);
      const upsert: PlanEdit[] = [];
      for (const [id, { after }] of moved) {
        const wall = wallById(id);
        if (wall) upsert.push(wallRunEdit(records, wall, activeStory, after));
      }
      const { follows } = followWalls(storeyObjects, facing, moved);
      return { batch: { upsert, revert: [] } as PlanEditBatch, follows };
    },
    [walls, wallById, records, activeStory, storeyObjects, facing],
  );

  const setWallLine = useCallback(
    (wallId: string, line: PlanLine, label: string) => {
      const wall = wallById(wallId);
      if (!wall) return;
      const moves: { wallId: string; which: "start" | "end"; to: PlanPoint }[] = [];
      if (planDistanceM(wall.start, line.start) > 1e-6) moves.push({ wallId, which: "start", to: line.start });
      if (planDistanceM(wall.end, line.end) > 1e-6) moves.push({ wallId, which: "end", to: line.end });
      if (moves.length === 0) return;
      const { batch, follows } = wallMoveChange(moves);
      change(label, batch, follows);
    },
    [wallById, wallMoveChange, change],
  );

  const setWallLength = useCallback(
    (wallId: string, lengthM: number, anchor: LengthAnchor) => {
      const wall = wallById(wallId);
      if (!wall || !(lengthM > 0)) return;
      setWallLine(wallId, resizedLine(lineOf(wall), lengthM, anchor), `${wall.label} length`);
    },
    [wallById, setWallLine],
  );

  const straighten = useCallback(
    (wallId: string) => {
      const wall = wallById(wallId);
      if (!wall) return;
      // Square to the REST of the house: the crooked wall itself must not
      // pull the axis it is being squared to.
      const others = walls.filter((w) => w.id !== wallId).map(lineOf);
      const axis = others.length > 0 ? dominantAxisDeg(others) : axisDeg;
      setWallLine(wallId, straightenedLine(lineOf(wall), axis), `straighten ${wall.label}`);
    },
    [wallById, walls, setWallLine, axisDeg],
  );

  const setWallProps = useCallback(
    (wallId: string, patch: { thicknessM?: number; heightM?: number }) => {
      const wall = wallById(wallId);
      if (!wall) return;
      change(`${wall.label} ${patch.thicknessM !== undefined ? "thickness" : "height"}`, {
        upsert: [wallPropsEdit(records, wall, activeStory, patch)],
        revert: [],
      });
    },
    [wallById, change, records, activeStory],
  );

  const setOpening = useCallback(
    (openingId: string, patch: OpeningModifyData) => {
      const opening = openingById(openingId);
      if (!opening) return;
      change(`${opening.label}`, { upsert: [openingEdit(records, opening, activeStory, patch)], revert: [] });
    },
    [openingById, change, records, activeStory],
  );

  const setRoomLabel = useCallback(
    (roomRef: string, label: string) => {
      const trimmed = label.trim();
      const held = recordFor(records, "room", roomRef);
      // A name set or cleared keeps the room's split or merge (roomEdit).
      const record = roomEdit(records, roomRef, activeStory, { label: trimmed === "" ? null : trimmed });
      if (record) change("room name", { upsert: [record], revert: [] });
      else if (held) change("room name", { upsert: [], revert: [{ targetKind: "room", targetId: roomRef }] });
    },
    [records, change, activeStory],
  );

  /** "Merge rooms": `fromRef` goes into `intoRef`, which keeps its name and identity. */
  const mergeRoom = useCallback(
    (fromRef: string, intoRef: string, label: string) => {
      const record = roomEdit(records, fromRef, activeStory, { mergeInto: intoRef });
      if (record) change(`merge ${label}`, { upsert: [record], revert: [] });
      input.onSelect(undefined);
    },
    [records, change, activeStory, input],
  );

  /** Take a room's split or merge back, keeping its name. */
  const undoRoomStructure = useCallback(
    (roomRef: string, label: string) => {
      const record = roomEdit(records, roomRef, activeStory, { split: null, mergeInto: null });
      if (record) change(`undo ${label}`, { upsert: [record], revert: [] });
      else change(`undo ${label}`, { upsert: [], revert: [{ targetKind: "room", targetId: roomRef }] });
    },
    [records, change, activeStory],
  );

  const remove = useCallback(
    (kind: "wall" | "opening", id: string) => {
      if (kind === "wall") {
        const wall = wallById(id);
        if (!wall) return;
        const hosted = openings.filter((o) => o.parentId === id).map((o) => o.id);
        const batch = deleteBatch(records, "wall", id, activeStory, hosted);
        const { orphaned } = followWalls(storeyObjects, facing, new Map(), new Set([id]));
        change(`delete ${wall.label}`, batch);
        setNotice(
          orphaned.length > 0
            ? `${wall.label} deleted. ${orphaned.length === 1 ? "An object was" : `${orphaned.length} objects were`} against it and now stand${orphaned.length === 1 ? "s" : ""} free — move or remove ${orphaned.length === 1 ? "it" : "them"}.`
            : `${wall.label} deleted.`,
        );
      } else {
        const opening = openingById(id);
        if (!opening) return;
        change(`delete ${opening.label}`, deleteBatch(records, "opening", id, activeStory));
        setNotice(`${opening.label} deleted.`);
      }
      if (selectedId === id) input.onSelect(undefined);
    },
    [wallById, openingById, openings, records, activeStory, storeyObjects, facing, change, selectedId, input],
  );

  /** Revert to scan: the element's correction goes. For a wall moved back, what is on it follows back. */
  const revert = useCallback(
    (kind: "wall" | "opening" | "room", id: string, label = "revert") => {
      const held = recordFor(records, kind, id);
      if (!held) return;
      let follows: ObjectFollow[] = [];
      if (kind === "wall" && held.action === "modify" && layer) {
        // Where the wall goes back to: the capture's own run.
        const wall = wallById(id);
        const raw = rawWallLine(layer, id);
        if (wall && raw) follows = followWalls(storeyObjects, facing, new Map([[id, { before: lineOf(wall), after: raw }]])).follows;
      }
      change(label, { upsert: [], revert: [{ targetKind: kind, targetId: id }] }, follows);
    },
    [records, layer, wallById, storeyObjects, facing, change],
  );

  // ----------------------------------------------------------- drags ----

  function snapCorner(point: PlanPoint, movingFrom: PlanPoint, wallId: string): PlanPoint {
    // Other corners, not the one being dragged (nor those joined to it).
    const others = walls
      .filter((w) => w.id !== wallId)
      .flatMap((w) => [w.start, w.end])
      .filter((p) => planDistanceM(p, movingFrom) > 0.03);
    const corner = nearestVertex(point, others, input.snapRadiusM());
    if (corner) return corner;
    // Square to the house: the wall's bearing within a few degrees of the main axis (or 45°) snaps onto it.
    const wall = wallById(wallId);
    if (!wall) return point;
    const fixed = planDistanceM(wall.start, movingFrom) < 1e-9 ? wall.end : wall.start;
    const bearing = planLineAngleDeg({ start: fixed, end: point });
    const snapped = axisDeg + Math.round((bearing - axisDeg) / 45) * 45;
    if (Math.abs(bearing - snapped) > AXIS_SNAP_DEG) return point;
    const r = planDistanceM(fixed, point);
    const t = (snapped * Math.PI) / 180;
    return { x: fixed.x + Math.cos(t) * r, y: fixed.y + Math.sin(t) * r };
  }

  function previewFor(drag: EditDrag, at: PlanPoint, event: PointerEvent): EditPreview | null {
    if (drag.kind === "opening") {
      const opening = openingById(drag.openingId);
      const host = wallById(drag.hostId);
      if (!opening || !host) return null;
      const L = planLineLengthM(lineOf(host));
      const w = opening.lengthM;
      const want = alongLineM(at, lineOf(host)) - drag.grabOffsetM;
      const offsetM = L >= w ? Math.min(Math.max(want, w / 2), L - w / 2) : L / 2;
      return {
        batch: { upsert: [openingEdit(records, opening, activeStory, { offsetM })], revert: [] },
        objects: new Map(),
      };
    }
    const wall = wallById(drag.wallId);
    if (!wall) return null;
    let moves: { wallId: string; which: "start" | "end"; to: PlanPoint }[];
    if (drag.kind === "wall") {
      let d = { x: at.x - drag.grab.x, y: at.y - drag.grab.y };
      if (!event.shiftKey) {
        // Square to itself: only the part of the drag across the wall counts.
        const len = planLineLengthM(lineOf(wall)) || 1;
        const n = { x: -(wall.end.y - wall.start.y) / len, y: (wall.end.x - wall.start.x) / len };
        const k = d.x * n.x + d.y * n.y;
        d = { x: n.x * k, y: n.y * k };
      }
      moves = [
        { wallId: wall.id, which: "start", to: { x: wall.start.x + d.x, y: wall.start.y + d.y } },
        { wallId: wall.id, which: "end", to: { x: wall.end.x + d.x, y: wall.end.y + d.y } },
      ];
    } else {
      const from = drag.which === "start" ? wall.start : wall.end;
      const raw = { x: from.x + (at.x - drag.grab.x), y: from.y + (at.y - drag.grab.y) };
      moves = [{ wallId: wall.id, which: drag.which, to: snapCorner(raw, from, wall.id) }];
    }
    const { batch, follows } = wallMoveChange(moves, event.altKey);
    return { batch, objects: new Map(follows.map((f) => [f.id, f.after] as const)) };
  }

  const handlers = useRef({ move: (_e: PointerEvent): void => {}, up: (): void => {}, cancel: (): void => {} });
  handlers.current = {
    move(event) {
      const drag = dragRef.current;
      if (!drag) return;
      if (event.buttons === 0) {
        handlers.current.up();
        return;
      }
      const dx = event.clientX - drag.clientX;
      const dy = event.clientY - drag.clientY;
      if (!drag.moved && Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD_PX) return;
      drag.moved = true;
      const at = input.planAt(event.clientX, event.clientY);
      if (!at) return;
      const next = previewFor(drag, at, event);
      previewRef.current = next;
      setPreview(next);
    },
    up() {
      const drag = dragRef.current;
      const done = previewRef.current;
      dragRef.current = null;
      previewRef.current = null;
      setPreview(null);
      if (!drag || !drag.moved) return;
      input.swallowNextClick();
      if (!done) return;
      const label =
        drag.kind === "opening"
          ? `move ${openingById(drag.openingId)?.label ?? "opening"}`
          : `move ${wallById(drag.wallId)?.label ?? "wall"}`;
      const follows: ObjectFollow[] = [];
      for (const [id, after] of done.objects) {
        const object = storeyObjects.find((o) => o.id === id);
        if (object && object.kind !== "pipe") follows.push({ id, before: object.geometry, after });
      }
      change(label, done.batch, follows);
    },
    cancel() {
      if (!dragRef.current) return;
      const moved = dragRef.current.moved;
      dragRef.current = null;
      previewRef.current = null;
      setPreview(null);
      if (moved) input.swallowNextClick();
    },
  };

  useEffect(() => {
    const move = (e: PointerEvent): void => handlers.current.move(e);
    const up = (): void => handlers.current.up();
    const key = (e: KeyboardEvent): void => {
      if (!dragRef.current) return;
      if (e.key === "Enter") {
        e.preventDefault();
        handlers.current.up();
      } else if (e.key === "Escape") {
        e.preventDefault();
        e.stopImmediatePropagation();
        handlers.current.cancel();
      }
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
    window.addEventListener("blur", up);
    window.addEventListener("keydown", key, true);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
      window.removeEventListener("blur", up);
      window.removeEventListener("keydown", key, true);
    };
  }, []);

  function startDrag(event: ReactPointerEvent<SVGElement>, make: (at: PlanPoint) => EditDrag | null): void {
    if (event.button !== 0 || !editing || tool) return;
    // Not a pan: the press belongs to the wall.
    event.stopPropagation();
    const at = input.planAt(event.clientX, event.clientY);
    if (!at) return;
    dragRef.current = make(at);
  }

  const onWallPointerDown = (id: string, event: ReactPointerEvent<SVGElement>): void => {
    startDrag(event, (at) => ({ kind: "wall", wallId: id, grab: at, clientX: event.clientX, clientY: event.clientY, moved: false }));
    if (editing && !tool && event.button === 0) input.onSelect(id);
  };

  const onEndPointerDown = (wallId: string, which: "start" | "end", event: ReactPointerEvent<SVGElement>): void => {
    startDrag(event, (at) => ({ kind: "end", wallId, which, grab: at, clientX: event.clientX, clientY: event.clientY, moved: false }));
  };

  const onOpeningPointerDown = (id: string, event: ReactPointerEvent<SVGElement>): void => {
    const opening = openingById(id);
    const host = opening?.parentId ? wallById(opening.parentId) : undefined;
    if (editing && !tool && event.button === 0) input.onSelect(id);
    if (!opening || !host) {
      if (editing && !tool) event.stopPropagation();
      return;
    }
    startDrag(event, (at) => ({
      kind: "opening",
      openingId: id,
      hostId: host.id,
      grabOffsetM: alongLineM(at, lineOf(host)) - openingOffsetM(opening, host),
      clientX: event.clientX,
      clientY: event.clientY,
      moved: false,
    }));
  };

  // ------------------------------------------------------------ tools ----

  /** Where an opening with `kind` in hand would go, the pointer at `point`. */
  const openingGhostAt = useCallback(
    (kind: PlanOpeningKind, point: PlanPoint): OpeningGhost | null => {
      const reach = Math.max(input.snapRadiusM() * 2, 0.3);
      let best: { wall: PlanSegment; along: number; d: number } | null = null;
      for (const wall of walls) {
        const line = lineOf(wall);
        const L = planLineLengthM(line);
        const along = Math.min(Math.max(alongLineM(point, line), 0), L);
        const foot = { x: wall.start.x + ((wall.end.x - wall.start.x) * along) / (L || 1), y: wall.start.y + ((wall.end.y - wall.start.y) * along) / (L || 1) };
        const d = planDistanceM(point, foot);
        if (d <= reach && (!best || d < best.d)) best = { wall, along, d };
      }
      if (!best) return null;
      const { wall } = best;
      const L = planLineLengthM(lineOf(wall));
      const widthM = Math.min(OPENING_DEFAULTS[kind].widthM, L);
      const offsetM = L >= widthM ? Math.min(Math.max(best.along, widthM / 2), L - widthM / 2) : L / 2;
      const ux = (wall.end.x - wall.start.x) / (L || 1);
      const uy = (wall.end.y - wall.start.y) / (L || 1);
      return {
        kind,
        wall,
        offsetM,
        widthM,
        start: { x: wall.start.x + ux * (offsetM - widthM / 2), y: wall.start.y + uy * (offsetM - widthM / 2) },
        end: { x: wall.start.x + ux * (offsetM + widthM / 2), y: wall.start.y + uy * (offsetM + widthM / 2) },
      };
    },
    [walls, input],
  );

  const [cursor, setCursor] = useState<PlanPoint | null>(null);
  const ghost = tool?.kind === "opening" && cursor ? openingGhostAt(tool.opening, cursor) : null;

  const snapWallPoint = (point: PlanPoint): PlanSnap =>
    snapPlanPoint(point, walls.flatMap((w) => [w.start, w.end]), walls, input.snapRadiusM());

  function placeWall(end: PlanPoint): void {
    const start = pendingStart;
    if (!start || !layer) return;
    if (planDistanceM(start, end) < 0.05) return;
    const room = roomAt(rooms, { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 });
    if (!room?.externalRef) {
      setNotice("A new wall needs a room on this storey to belong to.");
      armTool(null);
      return;
    }
    const id = newElementId();
    change("add a wall", {
      upsert: [
        {
          targetKind: "wall",
          targetId: id,
          action: "add",
          storey: activeStory,
          data: { roomRef: room.externalRef, start, end },
        },
      ],
      revert: [],
    });
    input.onSelect(id);
    armTool(null);
  }

  function placeSplit(end: PlanPoint): void {
    const start = pendingStart;
    if (!start || !layer) return;
    if (planDistanceM(start, end) < 0.3) return;
    const room = roomAt(rooms, { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 });
    if (!room?.externalRef) {
      setNotice("Draw the line across a room, from one of its walls to another.");
      armTool(null);
      return;
    }
    const record = roomEdit(records, room.externalRef, activeStory, { split: { start, end } });
    if (record) change(`split ${room.label}`, { upsert: [record], revert: [] });
    input.onSelect(undefined);
    armTool(null);
  }

  function placeOpening(g: OpeningGhost): void {
    const id = newElementId();
    const d = OPENING_DEFAULTS[g.kind];
    change(`add a ${g.kind}`, {
      upsert: [
        {
          targetKind: "opening",
          targetId: id,
          action: "add",
          storey: activeStory,
          data: { kind: g.kind, wallId: g.wall.id, offsetM: g.offsetM, widthM: g.widthM, heightM: d.heightM, sillHeightM: d.sillHeightM },
        },
      ],
      revert: [],
    });
    input.onSelect(id);
    armTool(null);
  }

  /** A click on the plan with an editor tool in hand. */
  function onToolClick(clientX: number, clientY: number): void {
    const point = input.planAt(clientX, clientY);
    if (!point || !tool) return;
    if (tool.kind === "wall" || tool.kind === "split") {
      const snap = snapWallPoint(point);
      if (!pendingStart) setPendingStart(snap.point);
      else if (tool.kind === "wall") placeWall(snap.point);
      else placeSplit(snap.point);
      return;
    }
    const g = openingGhostAt(tool.opening, point);
    // Not near a wall: nothing to put it in. The tool stays in hand.
    if (g) placeOpening(g);
  }

  function onToolMove(clientX: number, clientY: number): void {
    const point = input.planAt(clientX, clientY);
    setCursor(point);
    if (tool?.kind === "wall" || tool?.kind === "split") setHover(point ? snapWallPoint(point) : null);
  }

  function onToolLeave(): void {
    setCursor(null);
    setHover(null);
  }

  /**
   * Escape and Enter with an editor tool in hand (the plan's window listener
   * calls this). Escape takes back the wall's first end, then puts the tool
   * down. Enter drops what is on the pointer — the wall's second end, the
   * opening — where the pointer is, and puts the tool down; with nothing to
   * drop it just puts the tool down.
   */
  function onToolKey(event: KeyboardEvent): boolean {
    if (!tool) return false;
    if (event.key === "Escape") {
      event.preventDefault();
      if (pendingStart) setPendingStart(null);
      else armTool(null);
      return true;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      if (tool.kind === "wall" && pendingStart && hover) placeWall(hover.point);
      else if (tool.kind === "split" && pendingStart && hover) placeSplit(hover.point);
      else if (tool.kind === "opening" && ghost) placeOpening(ghost);
      else armTool(null);
      return true;
    }
    return false;
  }

  /** Delete, undo, redo and straighten, from the plan's own keyboard handler, in edit mode. */
  function onKey(event: ReactKeyboardEvent<HTMLElement>): boolean {
    if (!editing) return false;
    const mod = event.metaKey || event.ctrlKey;
    const key = event.key.toLowerCase();
    if (mod && key === "z") {
      event.preventDefault();
      if (event.shiftKey) redo();
      else undo();
      return true;
    }
    if (mod && key === "y") {
      event.preventDefault();
      redo();
      return true;
    }
    if (!selectedId || mod) return false;
    if (event.key === "Delete" || event.key === "Backspace") {
      if (wallById(selectedId)) {
        event.preventDefault();
        remove("wall", selectedId);
        return true;
      }
      if (openingById(selectedId)) {
        event.preventDefault();
        remove("opening", selectedId);
        return true;
      }
    }
    if (key === "s" && wallById(selectedId)) {
      event.preventDefault();
      straighten(selectedId);
      return true;
    }
    return false;
  }

  return {
    available,
    editing,
    setEditing,
    tool,
    armTool,
    pendingStart,
    hover,
    cursor,
    ghost,
    preview,
    dragging: dragRef.current !== null,
    notice,
    dismissNotice: () => setNotice(null),
    canUndo: undoStack.length > 0,
    canRedo: redoStack.length > 0,
    undoLabel: undoStack[undoStack.length - 1]?.label,
    redoLabel: redoStack[redoStack.length - 1]?.label,
    undo,
    redo,
    axisDeg,
    onWallPointerDown,
    onEndPointerDown,
    onOpeningPointerDown,
    onToolClick,
    onToolMove,
    onToolLeave,
    onToolKey,
    onKey,
    setWallLength,
    setWallProps,
    straighten,
    setOpening,
    setRoomLabel,
    mergeRoom,
    undoRoomStructure,
    remove,
    revert,
  };
}

export type PlanEditor = ReturnType<typeof usePlanEditor>;

/** A captured wall's own run, from the capture beneath the edit layer: where "revert" puts it. */
function rawWallLine(layer: PlanEditLayer, wallId: string): PlanLine | undefined {
  for (const room of capturedRooms(layer.capture)) {
    const wall = (room.walls ?? []).find((w) => w.identifier === wallId);
    if (wall) return surfacePlanLine(wall);
  }
  return undefined;
}
